import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import useTabs from "../../../utils/useTab";
import { styles } from "../../styles";
import useAnalysis from "../../../utils/useAnalysis";
import dayjs from "dayjs";
import { configButtonAnalysisFe55 } from "../../../utils/constants";
import { FormProvider, useFormContext } from "react-hook-form";
import {
  AnalysisPhasesFe55,
  Calculation,
  Counting,
  KeysTabsAnalysis,
  MeasurementTypes,
  PreparationTypes,
} from "@app/types/samples.types";
import { Remove } from "@app/components/Actions";
import {
  MAutocomplete,
  MButton,
  ResponsiveFormContainer,
} from "@app/components";
import useAuth from "@app/context/AuthContext";
import usePermission from "@app/context/PermissionContext";

const NavHistoryWithMeasurements: React.FC = () => {
  const theme = useTheme();
  const { tabValueAnalysis, setTabValueAnalysis } = useTabs();
  const [dialogData, setDialogData] = useState<any>({});
  const { isAdmin, isSuperAdmin } = usePermission();

  const {
    preparationsList,
    setPreparationsList,
    setCurrentPreparation,
    setCurrentMeasurement,
    setCurrentCounting,
    setCurrentCalculation,
    currentPreparation,
    currentMeasurement,
    currentCounting,
    currentCalculation,
    removeWithMeasure,
  } = useAnalysis();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { user } = useAuth();

  const form = useFormContext();
  const selectedAnalysis = form.getValues("typeAnalysis");

  const titleMesurement =
    selectedAnalysis?.codigo === KeysTabsAnalysis.BT ||
    selectedAnalysis?.codigo === KeysTabsAnalysis.BR
      ? "Nueva Medida de K por AA"
      : "Nueva Medida de Fe  AA";

  const filteredPreparationsList = preparationsList.filter((preparation) =>
    preparation?.analysis_type?.some(
      (analysis) => analysis.id === selectedAnalysis.id
    )
  );

  const fontSizeText = isSmallScreen
    ? "0.40rem"
    : isMediumScreen
    ? "0.50rem"
    : "0.60rem";

  const selectedStyle = {
    backgroundColor: theme.palette.action.selected,
    borderRadius: 4,
    padding: "0.5rem",
  };

  const selectedStyleActive = {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
    padding: "0.5rem",
  };

  const clearDataForm = () => {
    form.setValue("preparationSelected", null);
    form.setValue("countingSelected", null);
    form.setValue("measurementSelected", null);
  };

  const handleCloseModal = () => {
    clearDataForm();
    setDialogData({ active: false });
  };

  const handleOpenModal = (title: string) => {
    setDialogData({
      active: true,
      title: title,
    });
  };

  const onClickNewAction = () => {
    if (tabValueAnalysis === AnalysisPhasesFe55.PREPARATION) {
      const newPreparation = {
        id: undefined,
        analyst: user,
        start_date: dayjs().toISOString(),
        countings: [],
        aa_measures: [],
        analysis_type: [selectedAnalysis],
      };
      setPreparationsList([...preparationsList, newPreparation]);
      setCurrentPreparation(undefined, filteredPreparationsList.length, true);
    } else if (tabValueAnalysis === AnalysisPhasesFe55.MEASUREMENT) {
      handleOpenModal(titleMesurement);
    } else if (tabValueAnalysis === AnalysisPhasesFe55.COUNTING) {
      handleOpenModal("Nuevo Contaje");
    } else if (tabValueAnalysis === AnalysisPhasesFe55.CALCULATION) {
      handleOpenModal("Nuevo Cálculo");
    }
  };

  const preparationSelected = form.watch("preparationSelected");
  const measurementSelected = form.watch("measurementSelected");

  const typeAnalysis = form.watch("typeAnalysis");

  useEffect(() => {
    form.setValue("measurementSelected", null);
  }, [preparationSelected]);

  useEffect(() => {
    form.setValue("countingSelected", null);
  }, [measurementSelected]);

  const handlePreparationClick = (
    prepIndex: number,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation();
    const selectedPreparation = filteredPreparationsList[prepIndex];
    setCurrentPreparation(
      selectedPreparation.id,
      prepIndex,
      selectedPreparation?.aa_measures?.length === 0
    );
    setCurrentMeasurement(undefined, undefined);
    setCurrentCounting(undefined, undefined);
    setCurrentCalculation(undefined, undefined);
    setTabValueAnalysis(0); // Cambiar según el tab de Preparación
  };

  const handleMeasurementClick = (
    prepIndex: number,
    measureIndex: number,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation();
    const selectedMeasurement =
      filteredPreparationsList[prepIndex]?.aa_measures?.[measureIndex];
    setCurrentPreparation(filteredPreparationsList[prepIndex].id, prepIndex);

    setCurrentMeasurement(
      selectedMeasurement?.id,
      measureIndex,
      true
    );
    setCurrentCounting(undefined, undefined, undefined);
    setCurrentCalculation(undefined, undefined, undefined);
    setTabValueAnalysis(1);
  };

  const handleCountingClick = (
    prepIndex: number,
    measureIndex: number,
    countIndex: number,
    event?: React.MouseEvent
  ) => {
    if (event) event.stopPropagation();
    const selectedCouting =
      filteredPreparationsList[prepIndex].aa_measures?.[measureIndex].countings[
        countIndex
      ];

    setCurrentPreparation(
      filteredPreparationsList[prepIndex].id,
      prepIndex,
      false
    );
    setCurrentMeasurement(
      filteredPreparationsList[prepIndex].aa_measures?.[measureIndex].id,
      measureIndex,
      false
    );
    setCurrentCounting(
      selectedCouting?.id,
      countIndex,
      selectedCouting?.calculations &&
        selectedCouting?.calculations?.length === 0
    );
    setTabValueAnalysis(2); // Cambiar según el tab de Contaje
  };

  const handleCalculationClick = (
    prepIndex: number,
    measureIndex: number,
    countIndex: number,
    calcIndex: number,
    event?: React.MouseEvent
  ) => {
    if (event) event.stopPropagation();
    const selectedCalculation =
      filteredPreparationsList[prepIndex].aa_measures?.[measureIndex].countings[
        countIndex
      ].calculations[calcIndex];
    setCurrentPreparation(
      filteredPreparationsList[prepIndex].id,
      prepIndex,
      false
    );
    setCurrentMeasurement(
      filteredPreparationsList[prepIndex].aa_measures?.[measureIndex].id,
      measureIndex,
      false
    );
    setCurrentCounting(
      filteredPreparationsList[prepIndex].aa_measures?.[measureIndex].countings[
        countIndex
      ]?.id,
      countIndex,
      false
    );
    setCurrentCalculation(
      selectedCalculation?.id,
      calcIndex,
      selectedCalculation?.result
    );
    setTabValueAnalysis(3); // Cambiar según el tab de Cálculo
  };

  const handleModalSave = () => {
    const data = form.getValues();
    const { preparationSelected, measurementSelected, countingSelected } = data;

    if (tabValueAnalysis === AnalysisPhasesFe55.MEASUREMENT) {
      if (!preparationSelected || !preparationSelected.id) {
        form.setError("preparationSelected", {
          type: "manual",
          message: "La preparación es requerida",
        });
        return;
      }
      const updatedPreparations = preparationsList.map((preparation) =>
        preparation.id === preparationSelected.id
          ? {
              ...preparation,
              aa_measures: [
                ...(preparation.aa_measures || []),
                {
                  id: undefined,
                  measurement_date: dayjs().toISOString(),
                  countings: [],
                },
              ],
            }
          : preparation
      );

      setPreparationsList(updatedPreparations);
      const updatedPreparationIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        .findIndex((p) => p.id === preparationSelected.id);

      setCurrentPreparation(preparationSelected.id, updatedPreparationIndex);
      const selectedPreparation =
        updatedPreparations?.[
          preparationsList.findIndex((p) => p.id === preparationSelected.id)
        ];

      const measurementIndex = selectedPreparation?.aa_measures
        ? selectedPreparation.aa_measures.length - 1
        : 0;

      setCurrentMeasurement(undefined, measurementIndex, true);

      setTabValueAnalysis(1); // Cambiar a la pestaña de Medidas
    } else if (tabValueAnalysis === AnalysisPhasesFe55.COUNTING) {
      if (!preparationSelected || !preparationSelected.id) {
        form.setError("preparationSelected", {
          type: "manual",
          message: "La preparación es requerida",
        });
        return;
      }
      const updatedPreparations = preparationsList.map((preparation) =>
        preparation.id === preparationSelected.id
          ? {
              ...preparation,
              aa_measures: preparation.aa_measures?.map((measurement, index) =>
                measurement.id === measurementSelected.id
                  ? {
                      ...measurement,
                      countings: [
                        ...measurement.countings,
                        {
                          id: undefined,
                          start_date_time: dayjs().toISOString(),
                          end_date_time: dayjs().toISOString(),
                          counting_date: dayjs().toISOString(),
                          calculations: [],
                        },
                      ],
                    }
                  : measurement
              ),
            }
          : preparation
      );
      setPreparationsList(updatedPreparations);
      const updatedPreparationIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        .findIndex((p) => p.id === preparationSelected.id);
      const updatedMeasurementIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        [updatedPreparationIndex].aa_measures?.findIndex(
          (c) => c.id === measurementSelected.id
        );

      setCurrentPreparation(preparationSelected.id, updatedPreparationIndex);
      setCurrentMeasurement(measurementSelected.id, updatedMeasurementIndex);

      const validUpdatedMeasureIndex = updatedMeasurementIndex ?? 0;
      const selectedPreparation =
        updatedPreparations?.[
          preparationsList.findIndex((p) => p.id === preparationSelected.id)
        ];
      const selectedMeasure =
        selectedPreparation?.aa_measures?.[validUpdatedMeasureIndex];
      const coutingLength = selectedMeasure?.countings?.length;

      const currentCountingUpload = coutingLength ? coutingLength - 1 : 0;

      setCurrentCounting(undefined, currentCountingUpload, true);
      setTabValueAnalysis(2); // Cambiar a la pestaña de contaje
    } else if (tabValueAnalysis === AnalysisPhasesFe55.CALCULATION) {
      if (!preparationSelected || !countingSelected || !measurementSelected) {
        form.setError("countingSelected", {
          type: "manual",
          message: "La preparación, medición y conteo son requeridos",
        });
        return;
      }
      const updatedPreparations = preparationsList.map((preparation) =>
        preparation.id === preparationSelected.id
          ? {
              ...preparation,
              aa_measures: preparation.aa_measures?.map((measurement) =>
                measurement.id === measurementSelected.id
                  ? {
                      ...measurement,
                      countings: measurement.countings?.map((counting) =>
                        counting.id === countingSelected.id
                          ? {
                              ...counting,
                              calculations: [
                                ...(counting.calculations || []),
                                {
                                  id: undefined,
                                  start_date_time: dayjs().toISOString(),
                                  end_date_time: dayjs().toISOString(),
                                  counting_date: dayjs().toISOString(),
                                },
                              ],
                            }
                          : counting
                      ),
                    }
                  : measurement
              ),
            }
          : preparation
      );
      setPreparationsList(updatedPreparations);

      const updatedPreparationIndex = preparationsList.findIndex(
        (p) => p.id === preparationSelected.id
      );

      const updatedPreparaListIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        .findIndex((p) => p.id === preparationSelected.id);

      const updatedMeasurementIndex = preparationsList[
        updatedPreparationIndex
      ].aa_measures?.findIndex((c) => c.id === measurementSelected.id);

      const updatedMeasurementListIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        [updatedPreparaListIndex].aa_measures?.findIndex(
          (c) => c.id === measurementSelected.id
        ) as number;

      setCurrentPreparation(preparationSelected.id, updatedPreparaListIndex);
      setCurrentMeasurement(
        measurementSelected.id,
        updatedMeasurementListIndex
      );

      const updatedCountingIndex = preparationsList[
        updatedPreparationIndex
      ].aa_measures?.[updatedMeasurementIndex as number].countings?.findIndex(
        (c) => c.id === countingSelected.id
      );

      const updatedCountingListIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        [updatedPreparaListIndex]?.aa_measures?.[
          updatedMeasurementListIndex as number
        ].countings?.findIndex((c) => c.id === countingSelected.id);
      setCurrentCounting(countingSelected.id, updatedCountingListIndex);

      const selectedPreparationCalculation = updatedPreparations?.filter(
        (preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
      )[updatedPreparationIndex];
      const selectedMeasurement =
        selectedPreparationCalculation?.aa_measures?.[
          updatedMeasurementIndex as number
        ];

      const selectedCounting =
        selectedMeasurement?.countings?.[updatedCountingIndex as number];
      const calculationsLength = selectedCounting?.calculations?.length;

      if (calculationsLength !== undefined) {
        setCurrentCalculation(undefined, calculationsLength - 1);
      } else {
        setCurrentCalculation(undefined, 0);
      }

      setTabValueAnalysis(3); // Cambiar a la pestaña de calculo
    }

    handleCloseModal();
  };

  const autoSelectNav = () => {
    let pIndex;
    let mIndex;
    let coIndex;
    let caIndex;
    filteredPreparationsList.map(
      (preparation: PreparationTypes, prepIndex: number) => {
        if (preparation.id === currentPreparation.id) {
          pIndex = prepIndex;
          preparation.aa_measures?.map(
            (aa_measure: MeasurementTypes, measureIndex: number) => {
              if (aa_measure.id === currentMeasurement.id) {
                mIndex = measureIndex;
                aa_measure.countings?.map(
                  (counting: Counting, countIndex: number) => {
                    if (counting.id === currentCounting.id) {
                      coIndex = countIndex;
                      counting.calculations?.map(
                        (calculation: Calculation, calcIndex: number) => {
                          if (calculation.id === currentCalculation.id) {
                            caIndex = calcIndex;
                          }
                        }
                      );
                    }
                  }
                );
              }
            }
          );
        }
      }
    );
    if (caIndex !== undefined) {
      handleCalculationClick(pIndex, mIndex, coIndex, caIndex, undefined);
      return;
    }

    if (coIndex !== undefined) {
      handleCountingClick(pIndex, mIndex, coIndex, undefined);
      return;
    }

    if (mIndex !== undefined) {
      handleMeasurementClick(pIndex, mIndex, undefined);
      return;
    }

    if (pIndex !== undefined) {
      handlePreparationClick(pIndex, undefined);
      return;
    }
  };

  useEffect(() => {
    autoSelectNav();
  }, []);

  return (
    <>
      <Box sx={{ ...styles.navHistoryWrapper }}>
        {filteredPreparationsList.map(
          (preparation: PreparationTypes, prepIndex: number) => (
            <Box
              key={`preparation-${prepIndex}`}
              sx={{
                marginBottom: 2,
                ...(currentPreparation.index === prepIndex
                  ? tabValueAnalysis === AnalysisPhasesFe55.PREPARATION
                    ? selectedStyleActive
                    : selectedStyle
                  : {}),
              }}
              onClick={(event) => handlePreparationClick(prepIndex, event)}
            >
              <Box
                sx={{
                  fontSize: fontSizeText,
                  cursor: "pointer",
                  marginBottom: 1,
                }}
              >
                Preparación {prepIndex + 1}
                {preparation.aa_measures?.length === 0 && (
                  <Remove
                    onRemove={() =>
                      removeWithMeasure(
                        typeAnalysis.id,
                        "preparation",
                        currentPreparation?.id,
                        currentPreparation?.index
                      )
                    }
                  />
                )}
              </Box>

              <Box sx={{ paddingLeft: 2 }}>
                {preparation?.aa_measures?.map(
                  (aa_measure: MeasurementTypes, measureIndex: number) => (
                    <Box
                      key={`aa_measure-${measureIndex}`}
                      sx={{
                        marginBottom: 2,
                        ...(currentMeasurement?.index === measureIndex &&
                        currentPreparation.index === prepIndex
                          ? tabValueAnalysis === AnalysisPhasesFe55.MEASUREMENT
                            ? selectedStyleActive
                            : selectedStyle
                          : {}),
                      }}
                      onClick={(event) =>
                        handleMeasurementClick(prepIndex, measureIndex, event)
                      }
                    >
                      <Box
                        sx={{
                          fontSize: fontSizeText,
                          cursor: "pointer",
                          marginBottom: 1,
                        }}
                      >
                        Medida {measureIndex + 1}
                        {aa_measure?.countings?.length === 0 &&
                          (isAdmin || isSuperAdmin) && (
                            <Remove
                              onRemove={() =>
                                removeWithMeasure(
                                  typeAnalysis.id,
                                  "aa-measure",
                                  currentMeasurement?.id,
                                  currentPreparation?.index,
                                  currentMeasurement?.index
                                )
                              }
                            />
                          )}
                      </Box>

                      <Box sx={{ paddingLeft: 2 }}>
                        {aa_measure?.countings?.map(
                          (counting: Counting, countIndex: number) => (
                            <Box
                              key={`counting-${countIndex}`}
                              sx={{
                                marginBottom: 1,
                                ...(currentCounting?.index === countIndex &&
                                currentMeasurement?.index === measureIndex &&
                                currentPreparation.index === prepIndex
                                  ? tabValueAnalysis ===
                                    AnalysisPhasesFe55.COUNTING
                                    ? selectedStyleActive
                                    : selectedStyle
                                  : {}),
                              }}
                              onClick={(event) =>
                                handleCountingClick(
                                  prepIndex,
                                  measureIndex,
                                  countIndex,
                                  event
                                )
                              }
                            >
                              <Box
                                sx={{
                                  fontSize: fontSizeText,
                                  cursor: "pointer",
                                }}
                              >
                                Contaje {countIndex + 1}
                                {counting.calculations?.length === 0 &&
                                  (isAdmin || isSuperAdmin) && (
                                    <Remove
                                      onRemove={() =>
                                        removeWithMeasure(
                                          typeAnalysis.id,
                                          "counting",
                                          currentCounting?.id,
                                          currentPreparation?.index,
                                          currentMeasurement?.index,
                                          currentCounting?.index
                                        )
                                      }
                                    />
                                  )}
                              </Box>
                              <Box sx={{ paddingLeft: 2 }}>
                                {counting?.calculations?.map(
                                  (
                                    calculation: Calculation,
                                    calcIndex: number
                                  ) => (
                                    <Box
                                      key={`calculation-${calcIndex}`}
                                      sx={{
                                        marginBottom: 0,
                                        ...(currentCalculation?.index ===
                                          calcIndex &&
                                        currentMeasurement?.index ===
                                          measureIndex &&
                                        currentCounting?.index === countIndex &&
                                        currentPreparation.index === prepIndex
                                          ? tabValueAnalysis ===
                                            AnalysisPhasesFe55.CALCULATION
                                            ? selectedStyleActive
                                            : selectedStyle
                                          : {}),
                                      }}
                                      onClick={(event) =>
                                        handleCalculationClick(
                                          prepIndex,
                                          measureIndex,
                                          countIndex,
                                          calcIndex,
                                          event
                                        )
                                      }
                                    >
                                      <Box
                                        sx={{
                                          fontSize: fontSizeText,
                                          fontWeight: calculation?.result
                                            ?.to_reports
                                            ? "bold"
                                            : "normal",
                                          cursor: "pointer",
                                        }}
                                      >
                                        Cálculo {calcIndex + 1}
                                        {isAdmin || isSuperAdmin ? (
                                          <Remove
                                            onRemove={() =>
                                              removeWithMeasure(
                                                typeAnalysis.id,
                                                "calculation",
                                                currentCalculation?.id,
                                                currentPreparation?.index,
                                                currentMeasurement?.index,
                                                currentCounting?.index,
                                                currentCalculation?.index
                                              )
                                            }
                                          />
                                        ) : null}
                                      </Box>
                                    </Box>
                                  )
                                )}
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )
        )}
        <Box sx={{ ...styles.newPrepButton }}>
          {isAdmin || isSuperAdmin ? (
            <MButton
              variant="contained"
              sx={{ height: 1 }}
              onClick={onClickNewAction}
              text={
                configButtonAnalysisFe55[tabValueAnalysis].titleButton || ""
              }
            />
          ) : null}
        </Box>
      </Box>

      <Modal
        open={!!dialogData.active}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={{ ...styles.modalContainer }}>
          <FormProvider {...form}>
            <Grid item xs={12} my={2}>
              <Typography variant="tagText">{dialogData.title}</Typography>
              <Divider sx={{ mt: 1 }} />
            </Grid>

            <ResponsiveFormContainer columns={3}>
              <MAutocomplete
                name="preparationSelected"
                options={filteredPreparationsList
                  .filter((prep) => prep.id)
                  .map((prep, index) => ({
                    ...prep,
                    index,
                  }))}
                label="Seleccione una preparación"
                getOptionLabel={(option) => `Preparación ${option.index + 1}`}
                validation={{
                  required: "La preparación es requerida",
                }}
              />
              {preparationSelected &&
                (tabValueAnalysis === AnalysisPhasesFe55.COUNTING ||
                  tabValueAnalysis === AnalysisPhasesFe55.CALCULATION) && (
                  <MAutocomplete
                    key={preparationSelected?.id || "no-preparation"}
                    name="measurementSelected"
                    options={
                      preparationSelected?.aa_measures
                        .filter((count: Counting) => count.id)
                        .map((count: Counting, index: number) => ({
                          ...count,
                          index,
                        })) || []
                    }
                    label="Seleccione una medida"
                    getOptionLabel={(option: { id: string; index: number }) =>
                      `Medida ${option.index + 1}`
                    }
                    validation={{
                      required: "La medida es requerido",
                    }}
                  />
                )}
              {measurementSelected &&
                tabValueAnalysis === AnalysisPhasesFe55.CALCULATION && (
                  <MAutocomplete
                    key={preparationSelected?.id || "no-preparation"}
                    name="countingSelected"
                    options={
                      measurementSelected?.countings
                        .filter((counting: Counting) => counting.id)
                        .map((count: Counting, index: number) => ({
                          ...count,
                          index,
                        })) || []
                    }
                    label="Seleccione un contaje"
                    getOptionLabel={(option: { id: string; index: number }) =>
                      `Contaje ${option.index + 1}`
                    }
                    validation={{
                      required: "El contaje es requerido",
                    }}
                  />
                )}
            </ResponsiveFormContainer>
            <Box sx={{ ...styles.footerContainer }}>
              <MButton onClick={handleModalSave} text="Guardar" />
              <MButton
                onClick={handleCloseModal}
                text="Cerrar"
                variant="outlined"
              />
            </Box>
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
};

export default NavHistoryWithMeasurements;
